import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { ApiError } from "@odingard/sdk-client";
import { useAuth } from "../lib/auth";
import {
  evaluatePermission,
  missingPermissionFrom,
  type GovernancePermission,
  type ObservedDenial,
  type PermissionVerdict,
} from "./permissions";

export interface GovernancePermissions {
  /** Role reported by the Studio membership, for display only. */
  role: string | null;
  /** Advisory verdict used to decide how to present an action. */
  can: (key: GovernancePermission) => PermissionVerdict;
  /**
   * Records a backend denial so the same action stops being offered for the
   * rest of the session and can explain itself with the backend's own code and
   * permission key. Only a 403 naming this exact permission counts; anything
   * else (including segregation of duties) is a rule about one record, not a
   * missing capability.
   */
  observeDenial: (key: GovernancePermission, error: ApiError) => void;
  denials: Readonly<Record<string, ObservedDenial>>;
}

const PermissionContext = createContext<GovernancePermissions | null>(null);

/**
 * Advisory permission state for the Governance & Compliance module.
 *
 * The Policy AI backend is authoritative. This provider only mirrors what the
 * Studio membership advertises and remembers what the backend has already
 * refused; it never grants anything and never reimplements segregation of
 * duties (an author who may not approve their own version is a backend rule and
 * is enforced there).
 */
export function GovernancePermissionProvider({ children }: { children: ReactNode }) {
  const { activeOrg } = useAuth();
  const [denials, setDenials] = useState<Record<string, ObservedDenial>>({});

  const observeDenial = useCallback((key: GovernancePermission, error: ApiError) => {
    if (error.status !== 403) return;
    // Only a 403 that names this permission is a capability denial. Every other
    // 403 is a rule about one record (segregation of duties, route order, an
    // acceptance that must exist first) and must not turn the capability off:
    // the same user may hold the permission and act on a different exception a
    // second later.
    const named = missingPermissionFrom(error);
    if (named !== key) return;
    setDenials((current) => ({ ...current, [key]: { code: error.code, message: error.message } }));
  }, []);

  const membershipPermissions = useMemo(() => activeOrg?.permissions ?? [], [activeOrg]);

  const value = useMemo<GovernancePermissions>(
    () => ({
      role: activeOrg?.role ?? null,
      can: (key) => evaluatePermission(key, membershipPermissions, denials),
      observeDenial,
      denials,
    }),
    [activeOrg, membershipPermissions, denials, observeDenial],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions(): GovernancePermissions {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error("usePermissions must be used within GovernancePermissionProvider");
  return ctx;
}
